import {
  ApiError,
  CbDataRequestInit,
  NetworkError,
  NonThrowingFetch,
  ReReadableOkResponse,
} from '@cbhq/data-layer';
import { getLoggableError } from '@cbhq/identity-shared/utils';

export function isUnauthorizedError<
  T extends ReReadableOkResponse | { error: string } | NetworkError | ApiError | string,
>(response: T): boolean {
  if (response instanceof ApiError) {
    return response.status === 401;
  }

  if (response instanceof Error) {
    return response.message.includes('UNAUTHORIZED');
  }

  if (typeof response === 'string') {
    return response.split(':')[0] === 'UNAUTHORIZED';
  }

  if (typeof response === 'object' && 'error' in response) {
    return response?.error?.split(':')[0] === 'UNAUTHORIZED';
  }

  return false;
}

// HTTP methods that are safe to replay without side effects. A transient 401 on
// one of these can be retried once; anything that mutates server state
// (POST/PUT/PATCH/DELETE) is never retried, to avoid duplicate writes.
const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

function isRetriableRequest(init?: CbDataRequestInit): boolean {
  const method = init?.method?.toUpperCase() ?? 'GET';
  return IDEMPOTENT_METHODS.has(method);
}

export function unauthorizedErrorMiddleware(
  next: NonThrowingFetch,
  onUnauthorized: (error: Error) => void,
) {
  return async (url: string, init?: CbDataRequestInit) => {
    let response = await next(url, init);

    // A single transient 401 — a backend hiccup or a race against a server-side
    // session refresh — must not sign the user out. Retry an idempotent request
    // once before concluding the session is dead. A genuinely expired session
    // still returns 401 on the retry, so the sign-out path below is preserved;
    // only recoverable blips are absorbed. See IDFE-2898.
    if (isUnauthorizedError(response) && isRetriableRequest(init)) {
      response = await next(url, init);
    }

    if (isUnauthorizedError(response)) {
      const error = getLoggableError(response);
      onUnauthorized(error);
    }
    return response;
  };
}
